// src/pages/LicenseManagementPage/LicenseManagementPage.jsx

import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
    Paper, TableContainer, CircularProgress, Button, Tooltip, IconButton, Chip,
    Drawer, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import AddModeratorIcon from '@mui/icons-material/AddModerator';
import LinkIcon from '@mui/icons-material/Link';
import PublicIcon from '@mui/icons-material/Public';
import NotesIcon from '@mui/icons-material/Notes';
import { getPlatformDisplayName } from '../../components/PlatformUtils/PlatformUtils.jsx';

import { AuthContext } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import LicenseModal from '../../components/AdCreativeTools/LicenseModal';
import LicenseFilters from './LicenseFilters';
import translationEn from '../../i18n/supported_languages/en/translation.json';
import './LicenseManagementPage.css';

// Reusing History List Header styles for the sticky bar
import '../../components/HistoryListHeader/HistoryListHeader.css';

// Define constants for robust comparison
const ALL_TYPES = ['text', 'image', 'audio', 'video'];
// Note: 'expiring' is a UI concept derived from 'active' + date
const ALL_STATUSES = ['active', 'expiring', 'expired', 'inactive'];
const ALL_CONTENT_CATEGORIES = Object.keys(translationEn.contentMetadata.inclusion)

const INITIAL_FILTERS = {
    status: ALL_STATUSES,
    type: ALL_TYPES,
    searchQuery: '',
    contentCategories: ALL_CONTENT_CATEGORIES,
    safetyFlags: [],
    startDate: '',
    endDate: ''
};

const formatAnalysisDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    }).format(date);
};

// --- TOOLTIP CONTENT GENERATOR ---
// Helper: append "..." when backend preview is likely truncated at ~150 chars
const applyBackendEllipsis = (text) => {
    if (!text) return text;
    return `${text}${text.length > 150 - 10 ? '...' : ''}`;
};

const getLicenseTooltipContent = (lic, t) => {
    // Data Sources: 'ai_metadata' (fetched via subquery)
    const aiMetadata = lic.ai_metadata || {}; 
    const hasAiData = aiMetadata.summary || (aiMetadata.safety_flags && aiMetadata.safety_flags.length > 0);
    
    // If no AI data and no preview, don't show custom tooltip
    if (!hasAiData && !lic.content_preview) return null;

    const categoryText = aiMetadata.category 
        ? t(`contentMetadata.inclusion.${aiMetadata.category}`, { defaultValue: aiMetadata.category }) 
        : "";

    // Translate Content Type
    const contentTypeLabel = t(`contentInput.contentTypes.${lic.content_type}`, { defaultValue: lic.content_type });

    // --- Determine Media Section Content ---
    let mediaSection;
    if (lic.content_type === 'text') {
        mediaSection = (
            <div style={{ padding: '1rem', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                <p style={{
                    fontStyle: 'italic',
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.5,
                    fontSize: '0.9rem',
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                }}>
                    {lic.content_preview ? applyBackendEllipsis(lic.content_preview) : t('licensePage.table.preview.noPreview')}
                </p>
            </div>
        );
    } else if (lic.content_type === 'audio') {
        mediaSection = (
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100px', 
                backgroundColor: 'rgba(0,0,0,0.2)', 
                borderRadius: '6px',
                color: 'rgba(255,255,255,0.4)'
            }}>
                <AudiotrackIcon style={{ fontSize: '3rem', opacity: 0.7 }} />
            </div>
        );
    } else if (lic.content_preview) {
        // Assume valid image src for image/video content types if preview exists
        mediaSection = (
            <div style={{ textAlign: 'center', borderRadius: '4px', overflow: 'hidden', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                <img
                    src={lic.content_preview}
                    alt="Content preview"
                    style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }}
                />
            </div>
        );
    } else {
        mediaSection = (
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '80px', 
                backgroundColor: 'rgba(0,0,0,0.2)', 
                borderRadius: '6px',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.8rem',
                fontStyle: 'italic',
                textAlign: 'center',
                padding: '0 1rem'
            }}>
                {t('licensePage.table.tooltips.noPreview')}
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '400px', fontFamily: 'Roboto, sans-serif', fontSize: '0.875rem' }}>
            
            {/* 1. Header & Image Section */}
            <div style={{ marginBottom: '12px' }}>
                <h3 style={{
                    margin: '0 0 0.75rem 0',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: 'rgba(255, 255, 255, 0.85)',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                }}>
                    {t('licensePage.table.tooltips.previewHeader')}
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)', marginLeft: '4px' }}>
                        ({contentTypeLabel})
                    </span>
                </h3>
                
                {mediaSection}
            </div>

            {/* 2. Description & Category */}
            {aiMetadata.summary && (
                <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '4px', fontWeight: '600', paddingTop: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        {t('contentMetadata.descriptionLabel')}
                    </div>
                    <div style={{ fontSize: '0.85rem', lineHeight: '1.4', fontStyle: 'italic', color: 'rgba(255,255,255,0.9)' }}>
                        "{aiMetadata.summary}"
                        {categoryText && (
                            <span style={{ color: 'var(--secondary-color)', fontWeight: 700, fontStyle: 'normal', marginLeft: '8px' }}>
                                ({categoryText})
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* 3. Safety Alert */}
            {aiMetadata.safety_flags && aiMetadata.safety_flags.length > 0 && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '12px',
                    padding: '10px',
                    backgroundColor: 'rgba(139, 0, 0, 0.2)',
                    border: '1px solid rgba(139, 0, 0, 0.5)', 
                    borderRadius: '8px', 
                    color: '#F44336', 
                }}>
                    <ReportProblemOutlinedIcon
                        sx={{ fontSize: '1rem', color: '#F44336', marginTop: '2px', flexShrink: 0 }}
                    />
                    <div style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                        <strong style={{ color: '#F44336' }}>
                            {t('metaScores.safetyAlertPrefix')}
                        </strong>
                        {' '}
                        {aiMetadata.safety_flags.map((flag, i) => (
                            <span key={flag}>
                                {t(`contentMetadata.exclusion.${flag}`, { defaultValue: flag })}
                                {i < aiMetadata.safety_flags.length - 1 ? ', ' : ''}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};


const LicenseManagementPage = () => {
    const { t } = useTranslation();
    const { fetchWithAuth, user } = useContext(AuthContext);
    const { setIsPageBlurred, setIsFaqModalOpen } = useContext(UIContext);

    const [visibleCount, setVisibleCount] = useState(10);
    
    // Actions State
    const [activeModalLicense, setActiveModalLicense] = useState(null);
    const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    
    // Filters & UI State
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    
    // Context Modal State Declarations
    const [activeContextLicense, setActiveContextLicense] = useState(null);
    const [isContextModalOpen, setIsContextModalOpen] = useState(false);

    useEffect(() => {
        setIsPageBlurred(isLicenseModalOpen || isContextModalOpen);
    }, [isLicenseModalOpen, isContextModalOpen, setIsPageBlurred]);

    // Query Client for cache invalidation
    const queryClient = useQueryClient();

    // Fetch Licenses
    const { data: licenses = [], isLoading } = useQuery({
        queryKey: ['licenses'],
        queryFn: async () => {
            const res = await fetchWithAuth('/api/license/list');
            if (!res.ok) throw new Error('Failed to fetch licenses');
            return res.json();
        }
    });

    // Fetch Pricing Config (Cached indefinitely)
    const { data: pricingConfig = null } = useQuery({
        queryKey: ['billingConfig'],
        queryFn: async () => {
            const res = await fetchWithAuth('/api/config/billing');
            if (!res.ok) throw new Error('Failed to fetch pricing config');
            return res.json();
        },
        staleTime: Infinity
    });

    // Fetch Currency Rates (Cached for 1 hour)
    const { data: currencyRates = { USD: 1 } } = useQuery({
        queryKey: ['currencyRates'],
        queryFn: async () => {
            const res = await fetchWithAuth('/api/user/billing?limit=1');
            if (!res.ok) throw new Error('Failed to fetch rates');
            const data = await res.json();
            return data.currency_rates || { USD: 1 };
        },
        staleTime: 1000 * 60 * 60 
    });

    // Payment Mutation
    const paymentMutation = useMutation({
        mutationFn: async (durationDays) => {
            const response = await fetchWithAuth('/api/license/activate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    exact_hash: activeModalLicense.exact_hash,
                    content_type: activeModalLicense.content_type,
                    duration_days: durationDays,
                    context_hash: activeModalLicense.context_hash,
                    metadata: activeModalLicense.metadata 
                })
            });
            if (!response.ok) throw new Error("Payment Failed");
            return response.json();
        },
        onSuccess: () => {
            // Automatically re-fetch the licenses table to show the new active status!
            queryClient.invalidateQueries({ queryKey: ['licenses'] });
            setIsLicenseModalOpen(false);
        },
        onSettled: () => setIsPaying(false)
    });

    const filteredLicenses = useMemo(() => {
        let result = [...licenses];
        const now = new Date();
        now.setHours(0, 0, 0, 0); 

        // 1. Status Filter
        const selectedStatuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        const isFilteringStatus = selectedStatuses.length > 0 && 
                                  !selectedStatuses.includes('all') && 
                                  selectedStatuses.length < 4; 

        if (isFilteringStatus) {
            result = result.filter(l => {
                const endDate = l.end_date ? new Date(l.end_date) : null;
                if (endDate) endDate.setHours(0, 0, 0, 0);

                const isExpired = l.status === 'expired' || (endDate && endDate < now);
                const daysLeft = endDate ? Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)) : 999;
                const isExpiring = !isExpired && l.status === 'active' && daysLeft <= 14;
                const isActive = l.status === 'active' && !isExpired && !isExpiring;
                const isInactive = l.status === 'inactive' || !l.status;

                if (selectedStatuses.includes('active') && isActive) return true;
                if (selectedStatuses.includes('expiring') && isExpiring) return true;
                if (selectedStatuses.includes('expired') && isExpired) return true;
                if (selectedStatuses.includes('inactive') && isInactive) return true;
                return false;
            });
        }

        // 2. Type Filter
        if (filters.type && filters.type.length > 0 && filters.type.length < 4) {
            result = result.filter(l => filters.type.includes(l.content_type));
        }

        // 3. Search Filter
        if (filters.searchQuery) {
            const q = filters.searchQuery.toLowerCase();
            const hashRegex = /-[a-f0-9]{6}(\.[a-z0-9]+)$/i;
            result = result.filter(l => {
                const rawFilename = l.source_filename || l.metadata?.filename || '';
                const cleanFilename = rawFilename.replace(hashRegex, '$1');
                const batch = l.metadata?.batch_name || '';
                return cleanFilename.toLowerCase().includes(q) || batch.toLowerCase().includes(q);
            });
        }

        // 4. Content Category Filter
        if (filters.contentCategories && filters.contentCategories.length < ALL_CONTENT_CATEGORIES.length) {
            result = result.filter(l => {
                const category = l.ai_metadata?.category;
                if (!category) return false;
                return filters.contentCategories.includes(category);
            });
        }

        // 5. Safety Flags Filter
        if (filters.safetyFlags && filters.safetyFlags.length > 0) {
            result = result.filter(l => {
                const flags = l.ai_metadata?.safety_flags;
                if (!flags) return false;
                return flags.some(flag => filters.safetyFlags.includes(flag));
            });
        }

        // 6. Date range filtering
        const { startDate, endDate } = filters;
        if (startDate) {
            const start = new Date(startDate);
            result = result.filter(l => l.latest_analysis_timestamp && new Date(l.latest_analysis_timestamp) >= start);
        }
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            result = result.filter(l => l.latest_analysis_timestamp && new Date(l.latest_analysis_timestamp) <= end);
        }

        return result;
    }, [licenses, filters]);

    // Reset pagination when filters change
    useEffect(() => {
        setVisibleCount(10);
    }, [filters]);

    // --- HANDLERS ---
    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleResetFilters = () => {
        setFilters(INITIAL_FILTERS);
    };

    const areFiltersApplied = useMemo(() => {
        const isTypeFilterActive = filters.type.length < ALL_TYPES.length;
        // Check status length against full set
        const isStatusFilterActive = filters.status.length < ALL_STATUSES.length; 
        const isCategoryFilterActive = filters.contentCategories.length < ALL_CONTENT_CATEGORIES.length;
        const isSafetyFilterActive = filters.safetyFlags.length > 0;
        const isDateFilterActive = !!filters.startDate || !!filters.endDate;

        return isStatusFilterActive || isTypeFilterActive || isCategoryFilterActive || isSafetyFilterActive || !!filters.searchQuery || isDateFilterActive;
    }, [filters]);

    const currentDrawerWidth = areFiltersApplied ? 420 : 360;
    const handleLoadMore = () => setVisibleCount(prev => prev + 10);

    const handleExtend = (license) => {
        setActiveModalLicense(license);
        setIsLicenseModalOpen(true);
    };

    const handleView = (license) => {
        // 1. Base URL with ID
        let verificationUrl = `/verify?id=${license.exact_hash}`;

        // 2. Add Reference (Attribution)
        if (user?.public_id) {
            verificationUrl += `&ref=${user.public_id}`;
        }

        // 3. Add Context Hash (Critical for your issue)
        // The license object from the API includes 'context_hash' if the license is bound to specific context
        if (license.context_hash) {
            verificationUrl += `&ctx=${license.context_hash}`;
        }

        // 4. Add Default Flags
        // We force these to true so the verification page attempts to load them if available.
        // If the specific analysis didn't generate them, the backend handles the fallback gracefully.
        verificationUrl += '&regions=true&explanation=true';

        // Note: We do not append specific '&context=task_name' params here because the 
        // /api/license/list endpoint does not return the list of tasks. 
        // By omitting them, the Verification Page will automatically load ALL available tasks 
        // for this hash/context combination, which is the desired behavior.

        window.open(verificationUrl, '_blank');
    };

    const handleOpenContext = (license) => {
        setActiveContextLicense(license);
        setIsContextModalOpen(true);
    };

    const handlePayment = (durationDays) => {
        setIsPaying(true);
        paymentMutation.mutate(durationDays);
    };

    const handleModalDownload = useCallback(async () => {
        if (!activeModalLicense) return;

        const hash = activeModalLicense.exact_hash;
        const ref = user?.public_id;
        const ctx = activeModalLicense.context_hash;

        // Construct the URL precisely as we do in the Verification Modal
        let qrCodeUrl = `/api/qr/${hash}?regions=true&explanation=true`;
        if (ref) qrCodeUrl += `&ref=${ref}`;
        if (ctx) qrCodeUrl += `&ctx=${ctx}`;

        try {
            const response = await fetch(qrCodeUrl);
            if (!response.ok) throw new Error("Failed to fetch QR image");
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            
            // Use a friendly filename
            const shortHash = hash.substring(0, 8);
            a.download = `vetra-qr-${shortHash}.png`;
            
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
            // Clean up memory
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch (err) {
            console.error("Download failed:", err);
        }
    }, [activeModalLicense, user]);

    // --- RENDER HELPERS ---
    const getStatusChip = (status, endDateStr) => {
        // Handle Unlicensed / Inactive
        if (status === 'inactive' || !endDateStr) {
            return (
                <Chip 
                    label={t('licensePage.table.status.inactive')}
                    size="small" 
                    className="status-chip inactive" 
                />
            );
        }

        const endDate = new Date(endDateStr);
        const now = new Date();
        // Reset time to compare dates accurately
        now.setHours(0,0,0,0);
        const endCompare = new Date(endDate);
        endCompare.setHours(0,0,0,0);

        const isExpired = now > endCompare;

        if (status === 'expired' || isExpired) {
            return <Chip icon={<ErrorOutlineIcon />} label={t('licensePage.table.status.expired')} className="status-chip expired" size="small" />;
        }
        
        // Calculate days remaining (including today)
        const diffTime = endCompare - now;
        const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // UPDATED THRESHOLD: 14 Days
        if (daysLeft <= 14) {
            return <Chip icon={<WarningAmberIcon />} label={t('licensePage.table.status.expiring', { days: daysLeft })} className="status-chip warning" size="small" />;
        }
        
        return <Chip icon={<CheckCircleIcon />} label={t('licensePage.table.status.active')} className="status-chip active" size="small" />;
    };

    const getSourceDisplay = (meta, rawFilename, trueSource) => {
        // 1. Bulk Batch Check
        if (meta && meta.source === 'bulk') {
            return {
                main: t('licensePage.table.source.bulk'),
                sub: meta.batch_name ? `${t('licensePage.table.source.batchPrefix')} ${meta.batch_name}` : t('licensePage.table.source.unknownBatch')
            };
        }
        
        // 2. TRUE API Check (from billing database)
        if (trueSource === 'api_v1_async') {
            return { 
                main: t('licensePage.table.source.api'), 
                sub: t('licensePage.table.source.async') 
            };
        }
        if (trueSource === 'api_v1') {
            return { 
                main: t('licensePage.table.source.api'), 
                sub: t('licensePage.table.source.sync') 
            };
        }

        // 3. Fallback Name Check (for edge cases/legacy)
        if (rawFilename === 'api_async_upload') {
            return { 
                main: t('licensePage.table.source.api'), 
                sub: t('licensePage.table.source.async') 
            };
        }
        if (['api_upload', 'url_upload', 'text_snippet'].includes(rawFilename)) {
            return { 
                main: t('licensePage.table.source.api'), 
                sub: t('licensePage.table.source.sync') 
            };
        }

        // 4. Default UI
        return { main: t('licensePage.table.source.analyzer'), sub: t('licensePage.table.source.directUpload') };
    };

    // Generate unique options for the search autocomplete
    const filenameOptions = useMemo(() => {
        const options = new Set();
        
        // Regex to identify and remove the hash (e.g., "-a1b2c3.jpg" -> ".jpg")
        const hashRegex = /-[a-f0-9]{6}(\.[a-z0-9]+)$/i;

        licenses.forEach(lic => {
            // 1. Process Source Filename
            if (lic.source_filename) {
                // Replace the hash part with just the extension
                const cleanName = lic.source_filename.replace(hashRegex, '$1');
                options.add(cleanName);
            }

            // 2. Process Metadata Filename
            if (lic.metadata?.filename) {
                const cleanMetaName = lic.metadata.filename.replace(hashRegex, '$1');
                options.add(cleanMetaName);
            }

            // 3. Add Batch Name (No cleaning usually needed here)
            if (lic.metadata?.batch_name) {
                options.add(lic.metadata.batch_name);
            }
        });

        // Convert to sorted array, filtering out empties
        return Array.from(options).filter(Boolean).sort();
    }, [licenses]);

    return (
        <div className={`license-page-wrapper ${isFilterDrawerOpen ? 'content-blurred-subtle' : ''}`}>
            <Helmet><title>{t('licensePage.helmet.title')}</title></Helmet>
            
            <main 
                className="license-main-content"
                style={{ 
                    // Apply dynamic margin and width based on drawer state
                    marginLeft: (isFilterDrawerOpen && window.innerWidth > 900) ? `${currentDrawerWidth}px` : '0', 
                    width: (isFilterDrawerOpen && window.innerWidth > 900) ? `calc(100% - ${currentDrawerWidth}px)` : '100%',
                    transition: 'margin-left 0.3s ease, width 0.3s ease',
                    boxSizing: 'border-box'
                }}
            >
                <Header showDataFlowPreview={false} showBackButton={true} />
                
                <div className="license-content-inner">
                    <h1>{t('licensePage.header.title')}</h1>
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        {t('licensePage.header.subtitle')}
                    </p>

                    {/* --- HEADER ACTIONS (Sticky) --- */}
                    <div className="history-list-header-container license-sticky-header">
                        <div className="actions-row">
                            <Tooltip title={t('licensePage.stickyHeader.filterTooltip')} arrow>
                                <IconButton
                                    className="filter-icon-btn"
                                    onClick={() => setIsFilterDrawerOpen(true)}
                                >
                                    <FilterAltIcon />
                                </IconButton>
                            </Tooltip>

                            {areFiltersApplied && (
                                <Tooltip title={t('licensePage.stickyHeader.clearFiltersTooltip')} arrow>
                                    <Button
                                        className="clear-filters-link-btn effect-underline"
                                        onClick={handleResetFilters}
                                        variant="text"
                                        endIcon={<FilterAltOffIcon />}
                                    >
                                        {t('licensePage.stickyHeader.clearFiltersButton')}
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    </div>

                    {/* --- Title Row for Consistency --- */}
                    <div className="titles-row" style={{ marginTop: '4px' }}>
                        <span className="title-content-details">{t('licensePage.stickyHeader.titleDetails')}</span>
                        {/* Empty span to maintain flex spacing if needed, or remove if only left title is wanted */}
                        <span className="title-analysis-result"></span>
                    </div>

                    {/* --- TABLE LAYOUT --- */}
                    <Paper className="glass-card-v2 license-table-paper">
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="history-table-header">{t('licensePage.table.headers.preview')}</TableCell>
                                        <TableCell className="history-table-header">{t('licensePage.table.headers.fileDetails')}</TableCell>
                                        <TableCell className="history-table-header">{t('licensePage.table.headers.source')}</TableCell>
                                        <TableCell align="center" className="history-table-header">{t('licensePage.table.headers.status')}</TableCell>
                                        <TableCell align="center" className="history-table-header">{t('licensePage.table.headers.expires')}</TableCell>
                                        <TableCell align="center" className="history-table-header">{t('licensePage.table.headers.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                                                <CircularProgress color="secondary" />
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredLicenses.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ borderBottom: 'none', padding: 0 }}>
                                                <div className="history-empty filtered">
                                                    <p>{t('licensePage.table.empty.filtered')}</p>
                                                    <Button 
                                                        onClick={handleResetFilters} 
                                                        variant="outlined" 
                                                        className="load-more-button"
                                                    >
                                                        {t('licensePage.table.empty.clearButton')}
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredLicenses.slice(0, visibleCount).map((lic) => {
                                            const rawFilename = lic.source_filename || lic.metadata?.filename || 'Unknown Filename';
                                            const sourceInfo = getSourceDisplay(lic.metadata, rawFilename, lic.true_source);
                                            
                                            // Remove the 6-char hash added to prevent S3 collisions (e.g. "my-image-a1b2c3.png" -> "my-image.png")
                                            let filename = rawFilename.replace(/-[a-f0-9]{6}(\.[a-z0-9]+)$/i, '$1');
                                            
                                            // Translate Scraped Posts dynamically
                                            const scrapedMatch = filename.match(/^Scraped\s+(\w+)\s+Post$/i);
                                            if (scrapedMatch) {
                                                const platformName = scrapedMatch[1];
                                                const translatedPlatform = getPlatformDisplayName(platformName, t);
                                                filename = t('ThreatItem.scrapedPost', { 
                                                    platform: translatedPlatform, 
                                                    defaultValue: `Scraped ${translatedPlatform} Post` 
                                                });
                                            }

                                            // 1. Safety Flag Check
                                            const hasSafetyFlags = lic.ai_metadata?.safety_flags?.length > 0;
                                            
                                            // 2. Context Check
                                            const userContext = lic.user_context;
                                            const hasContext = userContext && (userContext.source_url || userContext.platform || userContext.user_notes);
                                            
                                            // Dynamic Tooltip Content
                                            const tooltipContent = getLicenseTooltipContent(lic, t);

                                            // --- FIX #2: Safety Tooltip Content ---
                                            const safetyTooltipContent = hasSafetyFlags ? (
                                                <div style={{ padding: '4px' }}>
                                                    <strong>{t('metaScores.safetyAlertPrefix')}</strong>
                                                    <hr style={{ margin: '6px 0 4px 0', border: 'none', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
                                                    <ul style={{ margin: '0', paddingLeft: '16px' }}>
                                                        {lic.ai_metadata.safety_flags.map(flag => (
                                                            <li key={flag}>
                                                                {t(`contentMetadata.exclusion.${flag}`, { defaultValue: flag })}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ) : "";

                                            return (
                                                <TableRow key={lic.license_id || `${lic.exact_hash}-${lic.history_context_hash || 'no-ctx'}`} className="license-table-row">
                                                    <TableCell>
                                                        <Tooltip 
                                                            title={tooltipContent} 
                                                            arrow 
                                                            placement="right"
                                                            PopperProps={{ className: "license-tooltip-popper" }}
                                                        >
                                                            <div className={`history-preview-box ${lic.content_type === 'text' ? 'text-mode' : ''}`}>
                                                                {lic.content_type === 'image' || lic.content_type === 'video' ? (
                                                                    <img src={lic.content_preview} alt="preview" className="history-preview-img" />
                                                                ) : lic.content_type === 'text' ? (
                                                                    <Box className="history-preview-placeholder text-preview">
                                                                        <Typography className="history-preview-text">
                                                                            {lic.content_preview || t('licensePage.table.preview.noPreview')}
                                                                        </Typography>
                                                                    </Box>
                                                                ) : (
                                                                    <Box className="history-preview-placeholder">
                                                                        {lic.content_type === 'audio' ? <AudiotrackIcon /> : <TextFieldsIcon />}
                                                                    </Box>
                                                                )}
                                                                
                                                                {/* Content Type Badge */}
                                                                <div className="type-badge-mini">
                                                                    {lic.content_type === 'video' && <VideocamIcon fontSize="inherit" />}
                                                                    {lic.content_type === 'image' && <ImageIcon fontSize="inherit" />}
                                                                    {lic.content_type === 'audio' && <AudiotrackIcon fontSize="inherit" />}
                                                                    {lic.content_type === 'text' && <TextFieldsIcon fontSize="inherit" />}
                                                                </div>
                                                            </div>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="history-item-details">
                                                            <div className="history-filename-row">
                                                                <Tooltip 
                                                                    title={filename} 
                                                                    arrow 
                                                                    placement="top" 
                                                                    disableHoverListener={filename.length <= 25}
                                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                                >
                                                                    <Typography variant="body2" className="license-filename" style={{ cursor: filename.length > 25 ? 'help' : 'default' }}>
                                                                        {filename}
                                                                    </Typography>
                                                                </Tooltip>
                                                                
                                                                {/* Context Button */}
                                                                {hasContext && (
                                                                    <Tooltip title={t('licensePage.table.tooltips.viewContext')} arrow>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleOpenContext(lic)}
                                                                            className="context-indicator-btn"
                                                                            sx={{ ml: 1 }} 
                                                                        >
                                                                            <AddModeratorIcon fontSize="inherit" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                            
                                                            {/* Show Last Analyzed Date */}
                                                            <Typography variant="caption" className="license-hash">
                                                                {formatAnalysisDate(lic.latest_analysis_timestamp)}
                                                            </Typography>
                                                            
                                                            {/* Safety Alert Badge */}
                                                            {hasSafetyFlags && (
                                                                <Tooltip title={safetyTooltipContent} arrow placement="top">
                                                                    <span className="safety-alert-badge">
                                                                        <ReportProblemOutlinedIcon />
                                                                        {t('historyItem.safetyAlertBadge')}
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ color: 'white' }}>
                                                            {sourceInfo.main}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                            {sourceInfo.sub}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {getStatusChip(lic.status, lic.end_date)}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                                        {lic.end_date 
                                                            ? new Date(lic.end_date).toLocaleDateString(undefined, { timeZone: 'UTC' }) 
                                                            : '-'
                                                        }
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                                            <Tooltip title={t('licensePage.table.tooltips.extend')}>
                                                                <IconButton onClick={() => handleExtend(lic)} size="small" className="table-action-btn primary">
                                                                    <QrCode2Icon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            
                                                            <Tooltip title={t('licensePage.table.tooltips.viewVerification')}>
                                                                <IconButton
                                                                    onClick={() => handleView(lic)}
                                                                    className="table-verification-btn"
                                                                >
                                                                    <img
                                                                        src="/static/vetraai_logo_grey.png"
                                                                        alt="Verification Shield"
                                                                        className="shield-icon-img"
                                                                    />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {visibleCount < filteredLicenses.length && (
                        <div className="load-more-container">
                            <Button onClick={handleLoadMore} variant="outlined" className="load-more-button">
                                {t('licensePage.loadMore')}
                            </Button>
                        </div>
                    )}
                </div>
                <Footer onOpenFaqModal={() => setIsFaqModalOpen(true)} showFaqButton={true} />
            </main>
            
            {/* Filter Drawer */}
            <Drawer 
                anchor="left" 
                open={isFilterDrawerOpen} 
                onClose={() => setIsFilterDrawerOpen(false)} 
                PaperProps={{ sx: { backgroundColor: 'var(--tertiary-color)' } }}
            >
                <Box 
                    sx={{ 
                        width: currentDrawerWidth,
                        padding: 2.5,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'width 0.3s ease' // Smooth animation
                    }} 
                    role="presentation"
                >
                    <LicenseFilters
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onResetFilters={handleResetFilters}
                        onClose={() => setIsFilterDrawerOpen(false)}
                        filenameOptions={filenameOptions}
                    />
                </Box>
            </Drawer>

            {/* License Payment/Extend Modal */}
            {activeModalLicense && (
                <>
                    <LicenseModal
                        isOpen={isLicenseModalOpen}
                        onClose={() => setIsLicenseModalOpen(false)}
                        onConfirm={handlePayment}
                        onDownload={handleModalDownload}
                        isPaying={isPaying}
                        isLoading={false}
                        currentLicense={{ ...activeModalLicense, expires_at: activeModalLicense.end_date }}
                        contentType={activeModalLicense.content_type}
                        pricingConfig={pricingConfig}
                        currencyRates={currencyRates}
                        selectedCurrency={user?.preferred_currency || 'USD'}
                        mode={['active', 'expired'].includes(activeModalLicense.status) ? 'extend' : 'activate'}
                    />
                </>
            )}

            {/* Context Detail Modal */}
            {activeContextLicense && activeContextLicense.user_context && (
                <Dialog
                    open={isContextModalOpen}
                    onClose={() => setIsContextModalOpen(false)}
                    PaperProps={{
                        sx: {
                            backgroundColor: '#ffffff !important',
                            color: '#1e293b !important',
                            minWidth: '400px',
                            borderRadius: '12px',
                            backgroundImage: 'none !important',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                        }
                    }}
                    style={{ zIndex: 1400 }}
                >
                    <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1, color: '#0f172a' }}>
                        <AddModeratorIcon sx={{ color: 'var(--secondary-color)' }} />
                        {t('licensePage.contextModal.title')}
                    </DialogTitle>

                    <DialogContent sx={{ mt: 2 }}>
                        {/* Source URL */}
                        {activeContextLicense.user_context.source_url && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 'bold' }}>
                                    <LinkIcon fontSize="small" /> {t('licensePage.contextModal.sourceUrl')}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        wordBreak: 'break-all',
                                        fontFamily: 'monospace',
                                        bgcolor: '#f1f5f9',
                                        color: '#334155',
                                        p: 1,
                                        borderRadius: 1,
                                        border: '1px solid #e2e8f0',
                                        '& a': {
                                            color: '#334155',
                                            textDecoration: 'none',
                                            transition: 'color 0.2s ease'
                                        },
                                        '& a:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}>
                                    <a href={activeContextLicense.user_context.source_url} target="_blank" rel="noopener noreferrer">
                                        {activeContextLicense.user_context.source_url}
                                    </a>
                                </Typography>
                            </Box>
                        )}

                        {/* Platform */}
                        {activeContextLicense.user_context.platform && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 'bold' }}>
                                    <PublicIcon fontSize="small" /> {t('licensePage.contextModal.platform')}
                                </Typography>
                                <Typography variant="body2" sx={{ bgcolor: '#f1f5f9', color: '#334155', p: 1, borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                    {getPlatformDisplayName(activeContextLicense.user_context.platform, t)}
                                </Typography>
                            </Box>
                        )}

                        {/* Notes */}
                        {activeContextLicense.user_context.user_notes && (
                            <Box>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 'bold' }}>
                                    <NotesIcon fontSize="small" /> {t('licensePage.contextModal.notes')}
                                </Typography>
                                <Typography variant="body2" sx={{ bgcolor: '#f1f5f9', color: '#334155', p: 1, borderRadius: 1, fontStyle: 'italic', border: '1px solid #e2e8f0' }}>
                                    "{activeContextLicense.user_context.user_notes}"
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                        <Button onClick={() => setIsContextModalOpen(false)} sx={{ color: '#64748b', '&:hover': { color: '#334155', backgroundColor: '#f1f5f9' } }}>
                            {t('historyItem.context.cancel')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
};

export default LicenseManagementPage;